import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Pagination,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import api from '../api/client';
import { getModernwashLegacyMemberDetail } from '../api/modernwashLegacy';
import QueryError from '../components/QueryError';
import GrantMembershipDialog, { canGrantMembership } from '../components/GrantMembershipDialog';

const productMap = {
  daily_subscription: '데일리',
  all_in_one_subscription: '올인원',
};

const statusMap = {
  active: { label: '활성', color: 'success' },
  cancellation_requested: { label: '해지예약', color: 'warning' },
  cancelled: { label: '취소', color: 'error' },
  refunded: { label: '환불', color: 'secondary' },
};

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mt: 4, mb: 1.5 }}>
    {children}
  </Typography>
);

const EmptyBox = ({ text }) => (
  <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
    <Typography color="text.secondary">{text}</Typography>
  </Paper>
);

const toLegacyMemberPageData = (payload) => {
  const data = payload?.data;
  if (!data?.member) return null;
  const paymentRecords = (data.payments?.records || []).map((record, index) => ({
    _id: record.id || `legacy-payment-${index}`,
    source: record.source,
    attemptedAt: record.date,
    productName: record.productName,
    amount: record.amount,
    payMethod: record.payMethod,
    status: record.status,
    reason: record.failureReason,
  }));
  const success = paymentRecords.filter((record) => record.status === 'success').length;

  return {
    member: {
      _id: data.member.id,
      memberNickname: data.member.nickname,
      memberEmail: data.member.email,
      memberPhone: data.member.phone,
      memberPoints: data.member.points,
      provider: data.member.provider,
    },
    vehicles: (data.vehicles || []).map((vehicle, index) => ({
      _id: vehicle.id || `legacy-vehicle-${index}`,
      licensePlate: vehicle.licensePlate,
      maker: vehicle.maker,
      model: vehicle.model,
      color: vehicle.color,
    })),
    subscriptions: (data.subscriptions || []).map((subscription, index) => ({
      _id: subscription.id || `legacy-subscription-${index}`,
      productId: subscription.productId,
      status: subscription.status,
      washCount: subscription.washCount,
      nextPaymentDate: subscription.nextPaymentDate,
      expirationDate: subscription.expirationDate,
      vehicleLicensePlate: subscription.licensePlate,
    })),
    cards: (data.cards || []).map((card, index) => ({
      _id: card.id || `legacy-card-${index}`,
      cardCompany: card.company,
      cardNumberMasked: card.number,
      isDefault: card.isDefault,
    })),
    washData: {
      total: data.wash?.total || 0,
      records: (data.wash?.records || []).map((record, index) => ({
        _id: record.id || `legacy-wash-${index}`,
        source: record.source,
        usedAt: record.usedAt,
        licensePlate: record.licensePlate,
        washerId: record.washerId,
        productName: record.productName,
        storeName: record.location,
      })),
    },
    payData: {
      total: data.payments?.total || 0,
      records: paymentRecords,
      counts: { all: paymentRecords.length, success, failure: paymentRecords.length - success },
    },
  };
};

/**
 * `legacyOnly` is the detail view used by /modernwash-ops.
 * It calls the V1 read-only adapter directly instead of trying Mogoo first,
 * so this page cannot be mistaken for a merged account or entitlement record.
 */
const MemberDetailPage = ({ legacyOnly = false, backPath = '/members' }) => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [cards, setCards] = useState([]);
  const [washData, setWashData] = useState({ total: 0, records: [] });
  const [washPage, setWashPage] = useState(1);
  // 결제 시도 이력 — 외주(payments) + 자체(SubscriptionHistory) 통합
  const [payData, setPayData] = useState({ total: 0, records: [], counts: { all: 0, success: 0, failure: 0 } });
  const [payPage, setPayPage] = useState(1);
  const [payStatus, setPayStatus] = useState('all'); // 'all' | 'success' | 'failure'
  const [loading, setLoading] = useState(true);
  // native member ID가 아닌 기존 ModernWash V1 ID는 읽기 전용 어댑터로 표시한다.
  const [legacySource, setLegacySource] = useState(null);
  /**
   * 섹션별 조회 실패 — key: member | vehicles | subscriptions | cards | wash | pay
   * 실패한 섹션만 QueryError 를 띄우고 나머지는 정상 렌더한다.
   * 상태를 6개로 늘리는 대신 키 객체 하나로 둔다.
   */
  const [errors, setErrors] = useState({});
  const setSectionError = (key, err) =>
    setErrors((prev) => ({ ...prev, [key]: err || undefined }));
  /** allSettled 결과 → 데이터 세팅 + 실패 기록. 실패 시 fallback 으로 비운다. */
  const applySection = (key, settled, setter, fallback) => {
    if (settled.status === 'fulfilled') {
      setter(settled.value.data || fallback);
      setSectionError(key, null);
    } else {
      setter(fallback);
      setSectionError(key, settled.reason);
    }
  };
  const [pointsEditing, setPointsEditing] = useState(false);
  const [pointsInput, setPointsInput] = useState('');
  // 이용권 수동 지급 — 회원이 고정된 상태로 다이얼로그를 연다 (master/manager만)
  const [grantOpen, setGrantOpen] = useState(false);
  const canGrant = canGrantMembership();
  const WASH_LIMIT = 10;
  const PAY_LIMIT = 10;

  useEffect(() => {
    fetchMemberDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  useEffect(() => {
    if (legacySource === false) fetchWashRecords(washPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [washPage, legacySource]);

  useEffect(() => {
    if (legacySource === false) fetchPaymentAttempts(payPage, payStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPage, payStatus, legacySource]);

  // 필터 변경 시 페이지를 1로 리셋
  const handlePayStatusChange = (_, newStatus) => {
    if (!newStatus) return; // null이면 무시 (toggle 해제 방지)
    setPayStatus(newStatus);
    setPayPage(1);
  };

  /**
   * 회원 상세는 호출이 6개다. 이전에는 앞 4개를 Promise.all 로 묶어
   * **하나만 실패해도 member 가 null 로 남아 "회원을 찾을 수 없습니다"** 가 떴다.
   * 결제 분쟁 대응 중에 "그런 회원 없습니다"라고 답하게 되는 경로였다.
   *
   * allSettled 로 바꿔 섹션별로 성패를 기록한다 — 실패한 섹션만 QueryError 를 띄우고
   * 나머지는 정상 렌더한다. 요청 수는 그대로 4개다.
   */
  const fetchMemberDetail = async () => {
    setLoading(true);
    setLegacySource(null);
    // finally 로 감싼다 — 상태 반영 중 예외가 나도 화면이 "로딩 중..." 에 갇히지 않게 한다.
    // (allSettled 자체는 reject 하지 않지만, 원래 코드가 주던 보장을 유지한다)
    try {
      if (legacyOnly) {
        try {
          const legacy = toLegacyMemberPageData(await getModernwashLegacyMemberDetail(memberId));
          if (!legacy) throw new Error('ModernWash 회원 응답 형식이 올바르지 않습니다.');
          setMember(legacy.member);
          setVehicles(legacy.vehicles);
          setSubscriptions(legacy.subscriptions);
          setCards(legacy.cards);
          setWashData(legacy.washData);
          setPayData(legacy.payData);
          setErrors({});
          setLegacySource(true);
        } catch (legacyError) {
          setMember(null);
          setSectionError('member', legacyError);
        }
        return;
      }

      const [memberRes, vehiclesRes, subsRes, cardsRes] = await Promise.allSettled([
        api.get(`/member/admin/members/${memberId}`),
        api.get(`/member/admin/members/${memberId}/vehicles`),
        api.get(`/member/admin/members/${memberId}/subscriptions`),
        api.get(`/member/admin/members/${memberId}/cards`),
      ]);

      // 회원 기본 정보 — 404 는 "없는 회원"이고 그 외 실패는 "조회 오류"다. 둘을 섞지 않는다.
      if (memberRes.status === 'fulfilled') {
        setMember(memberRes.value.data);
        setSectionError('member', null);
        setLegacySource(false);
      } else {
        // 같은 URL을 쓰는 기존 ModernWash 회원은 Mogu DB에 없을 수 있다. 그 경우에만
        // 백엔드의 live read-only 어댑터를 시도한다. 임의의 오류/권한 오류를 숨기지 않는다.
        const nativeStatus = memberRes.reason?.response?.status;
        if (nativeStatus === 404) {
          try {
            const legacy = toLegacyMemberPageData(await getModernwashLegacyMemberDetail(memberId));
            if (!legacy) throw new Error('ModernWash 회원 응답 형식이 올바르지 않습니다.');
            setMember(legacy.member);
            setVehicles(legacy.vehicles);
            setSubscriptions(legacy.subscriptions);
            setCards(legacy.cards);
            setWashData(legacy.washData);
            setPayData(legacy.payData);
            setErrors({});
            setLegacySource(true);
          } catch (legacyError) {
            setMember(null);
            setSectionError('member', legacyError);
          }
        } else {
          setMember(null);
          setSectionError('member', memberRes.reason);
        }
      }

      if (memberRes.status === 'fulfilled') {
        applySection('vehicles', vehiclesRes, setVehicles, []);
        applySection('subscriptions', subsRes, setSubscriptions, []);
        applySection('cards', cardsRes, setCards, []);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchWashRecords = async (page) => {
    try {
      const res = await api.get(`/member/admin/members/${memberId}/wash-records`, {
        params: { page, limit: WASH_LIMIT },
      });
      setWashData(res.data);
      setSectionError('wash', null);
    } catch (err) {
      // 실패를 "0건"으로 보이게 하지 않는다 — 목록을 비우고 실패 사실을 남긴다
      setWashData({ total: 0, records: [] });
      setSectionError('wash', err);
    }
  };

  const fetchPaymentAttempts = async (page, status) => {
    try {
      const res = await api.get(`/member/admin/members/${memberId}/payment-attempts`, {
        params: { page, limit: PAY_LIMIT, status },
      });
      setPayData(res.data);
      setSectionError('pay', null);
    } catch (err) {
      setPayData({ total: 0, records: [], counts: { all: 0, success: 0, failure: 0 } });
      setSectionError('pay', err);
    }
  };

  const handlePointsSave = async () => {
    const val = parseInt(pointsInput);
    if (isNaN(val) || val < 0) {
      alert('올바른 포인트 값을 입력해주세요.');
      return;
    }
    try {
      await api.patch(`/member/admin/members/${memberId}/points`, { memberPoints: val });
      setMember((prev) => ({ ...prev, memberPoints: val }));
      setPointsEditing(false);
    } catch (err) {
      alert('포인트 수정에 실패했습니다.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">로딩 중...</Typography>
      </Box>
    );
  }

  // 회원 기본 정보를 못 불러온 경우 — 404(없는 회원)와 조회 실패(네트워크·5xx·403)를 구분한다.
  // 이전에는 둘 다 "회원을 찾을 수 없습니다"로 보여, 네트워크 한 번 끊긴 것을
  // 운영자가 "그런 회원 없음"으로 오인할 수 있었다.
  if (!member) {
    const notFound = errors.member?.response?.status === 404;
    return (
      <Box sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ color: '#555', mb: 2 }}>
          목록으로
        </Button>
        {errors.member && !notFound ? (
          <QueryError error={errors.member} onRetry={fetchMemberDetail} context="회원 정보" />
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">회원을 찾을 수 없습니다.</Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ color: '#555' }}
        >
          목록으로
        </Button>
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, borderBottom: '3px solid #1976d2', pb: 1 }}>
        멤버 상세 정보
      </Typography>
      {legacySource && (
        <Box sx={{ mt: 1.25 }}>
          <Chip label="ModernWash V1 · 전주점 기존 데이터 · 읽기 전용" color="info" size="small" />
          <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 12.5, lineHeight: 1.55 }}>
            이 화면은 V1 원천을 읽기 전용으로 조회한 과거 계약·이력입니다. 신규 가입·결제·세차 권리는 Mogoo에서 별도로 판단하며, 여기의 활성 상태로 현장 승인을 결정하지 않습니다.
          </Typography>
        </Box>
      )}

      {/* 멤버 정보 */}
      <Paper sx={{ p: 3, mt: 3 }} elevation={0} variant="outlined">
        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
          멤버 정보
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">이름</Typography>
            <Typography variant="body1">{member.memberNickname || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">이메일</Typography>
            <Typography variant="body1">{member.memberEmail || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">전화번호</Typography>
            <Typography variant="body1">{member.memberPhone || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">포인트</Typography>
            {legacySource ? (
              <Typography variant="body1">{member.memberPoints || 0}점</Typography>
            ) : pointsEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <TextField
                  size="small"
                  type="number"
                  value={pointsInput}
                  onChange={(e) => setPointsInput(e.target.value)}
                  slotProps={{ htmlInput: { min: 0 } }}
                  sx={{ width: 100 }}
                  autoFocus
                />
                <IconButton size="small" color="primary" onClick={handlePointsSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setPointsEditing(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1">{member.memberPoints || 0}점</Typography>
                <IconButton
                  size="small"
                  onClick={() => { setPointsInput(String(member.memberPoints || 0)); setPointsEditing(true); }}
                  sx={{ color: '#999' }}
                >
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">가입경로</Typography>
            <Typography variant="body1">{member.provider || '-'}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* 구독 정보 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          구독 정보
        </Typography>
        {canGrant && !legacySource && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CardMembershipIcon />}
            onClick={() => setGrantOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            이용권 부여
          </Button>
        )}
      </Box>
      {errors.subscriptions ? (
        <QueryError error={errors.subscriptions} onRetry={fetchMemberDetail} context="구독 정보" />
      ) : subscriptions.length > 0 ? (
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700 }}>차량번호</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>상품</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>상태</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>세차 횟수</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>다음 결제일</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>만료일</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => {
                const status = statusMap[sub.status] || { label: sub.status, color: 'default' };
                return (
                  <TableRow key={sub._id}>
                    <TableCell>
                      {sub.vehicleId?.licensePlate || sub.vehicleLicensePlate || '-'}
                    </TableCell>
                    <TableCell align="center">
                      {productMap[sub.productId] || sub.productId}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell align="center">{sub.washCount ?? 0}회</TableCell>
                    <TableCell align="center">{formatDate(sub.nextPaymentDate)}</TableCell>
                    <TableCell align="center">{formatDate(sub.expirationDate)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyBox text="활성 구독이 없습니다." />
      )}

      {/* 세차 이용 내역 — 외주(wash_histories) + 자체(WashRecord) 통합 */}
      <SectionTitle>세차 이용 내역 ({washData.total}건)</SectionTitle>
      {errors.wash ? (
        <QueryError error={errors.wash} onRetry={() => fetchWashRecords(washPage)} context="세차 이용 내역" />
      ) : washData.records.length > 0 ? (
        <>
          <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 70 }}>출처</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>이용일시</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>차량번호</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>상품/세차기</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>지점/위치</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {washData.records.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell align="center">
                      <Chip
                        label={r.source === 'internal' ? 'Mogoo' : 'ModernWash V1'}
                        size="small"
                        color={r.source === 'internal' ? 'primary' : 'default'}
                        variant={r.source === 'internal' ? 'filled' : 'outlined'}
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{formatDateTime(r.usedAt)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{r.licensePlate || '-'}</TableCell>
                    <TableCell align="center" sx={{ color: '#555', fontSize: 13 }}>
                      {/* Mogoo 기록은 세차기 ID, V1 이력은 당시 상품명을 표시한다. */}
                      {r.source === 'internal' ? (r.washerId || '-') : (r.productName || '-')}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#555', fontSize: 13 }}>
                      {r.storeName || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {Math.ceil(washData.total / WASH_LIMIT) > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(washData.total / WASH_LIMIT)}
                page={washPage}
                onChange={(_, v) => setWashPage(v)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      ) : (
        <EmptyBox text="세차 이용 내역이 없습니다." />
      )}

      {/* 결제 시도 이력 — 외주(payments) + 자체(SubscriptionHistory) 통합 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          결제 시도 이력 ({payData.counts?.all ?? 0}건)
        </Typography>
        <ToggleButtonGroup
          value={payStatus}
          exclusive
          onChange={handlePayStatusChange}
          size="small"
          color="primary"
        >
          <ToggleButton value="all">전체 {payData.counts?.all ?? 0}</ToggleButton>
          <ToggleButton value="success">성공 {payData.counts?.success ?? 0}</ToggleButton>
          <ToggleButton value="failure">실패 {payData.counts?.failure ?? 0}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {errors.pay ? (
        <QueryError error={errors.pay} onRetry={() => fetchPaymentAttempts(payPage, payStatus)} context="결제 시도 이력" />
      ) : payData.records.length > 0 ? (
        <>
          <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 70 }}>출처</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>일시</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>상품</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>금액</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>결제수단</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>결과</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>실패 사유</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payData.records.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell align="center">
                      <Chip
                        label={r.source === 'internal' ? 'Mogoo' : 'ModernWash V1'}
                        size="small"
                        color={r.source === 'internal' ? 'primary' : 'default'}
                        variant={r.source === 'internal' ? 'filled' : 'outlined'}
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{formatDateTime(r.attemptedAt)}</TableCell>
                    <TableCell>{r.productName || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {r.amount != null ? `${r.amount.toLocaleString()}원` : '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: '#555' }}>{r.payMethod || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={r.status === 'success' ? '성공' : '실패'}
                        size="small"
                        color={r.status === 'success' ? 'success' : 'error'}
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'error.main' }}>{r.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {Math.ceil(payData.total / PAY_LIMIT) > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(payData.total / PAY_LIMIT)}
                page={payPage}
                onChange={(_, v) => setPayPage(v)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      ) : (
        <EmptyBox text={
          payStatus === 'all' ? '결제 시도 이력이 없습니다.' :
          payStatus === 'success' ? '성공한 결제가 없습니다.' :
          '실패한 결제가 없습니다.'
        } />
      )}

      {/* 등록된 카드 목록 */}
      <SectionTitle>등록된 카드</SectionTitle>
      {errors.cards ? (
        <QueryError error={errors.cards} onRetry={fetchMemberDetail} context="등록된 카드" />
      ) : cards.length > 0 ? (
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell align="center" sx={{ fontWeight: 700 }}>카드사</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>카드번호</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>사용여부</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cards.map((card) => {
                const cardIdStr = card._id?.toString();
                const isUsed = subscriptions.some(
                  (sub) => sub.cardId?.toString() === cardIdStr
                );
                return (
                  <TableRow key={card._id}>
                    <TableCell align="center">{card.cardCompany || '-'}</TableCell>
                    <TableCell align="center">{card.cardNumberMasked || '-'}</TableCell>
                    <TableCell align="center">
                      {isUsed ? <Chip label="사용중" color="success" size="small" /> : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyBox text="등록된 카드가 없습니다." />
      )}

      {/* 등록된 차량 목록 */}
      <SectionTitle>등록된 차량</SectionTitle>
      {errors.vehicles ? (
        <QueryError error={errors.vehicles} onRetry={fetchMemberDetail} context="등록된 차량" />
      ) : vehicles.length > 0 ? (
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table sx={{ "& td[align=\"center\"], & th[align=\"center\"]": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700 }}>차량번호</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>차종</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>색상</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow key={v._id}>
                  <TableCell>{v.licensePlate || '-'}</TableCell>
                  <TableCell align="center">{v.model || '-'}</TableCell>
                  <TableCell align="center">{v.color || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyBox text="등록된 차량이 없습니다." />
      )}

      {/* 이용권 수동 지급 — 이 회원으로 고정 */}
      {!legacySource && (
        <GrantMembershipDialog
          open={grantOpen}
          onClose={() => setGrantOpen(false)}
          member={{ ...member, _id: member._id || memberId }}
          onGranted={() => {
            alert('이용권이 지급되었습니다.');
            fetchMemberDetail();
          }}
        />
      )}
    </Box>
  );
};

export default MemberDetailPage;
